import { createSelector } from "@reduxjs/toolkit";
import { imageSelectors } from "./imageSlice";
import { StoreState } from "../store";
import { CustomizationObjectType, CustomizationOutput, ValidationStatus } from "../global-types/customization-types";
import { BackendValidationErrorViewModel, FormValidationError, FormValidationErrorSeverity } from "../global-types/store-errors";
import { generateCustomizationTreeViewItems, SelectorWithInputArg } from "../global-types/customizationSelectors";
import { isImageCustomizationOutput } from "./image-types";
import { CommonImageSelectors } from "../global-types/modelTypeSelectorHelper/commonImageSelectors";


const commonSelectors = new CommonImageSelectors();

export const imageCommonSelectors = {
    selectDeprecatedModels: (state: StoreState) => commonSelectors.selectDeprecatedModels(state.image),
    selectNonLicensedModels: (state: StoreState) => commonSelectors.selectNonLicensedModels(state.image),
    selectUserHiddenModels: (state: StoreState) => commonSelectors.selectUserHiddenModels(state.image),
    selectLatestModels: (state: StoreState) => commonSelectors.selectLatestModels(state.image),
    selectModelsInAlphabeticalOrder: (state: StoreState) => commonSelectors.selectModelsInAlphabeticalOrder(state.image),
    selectAllVersionTagsInOrder: (state: StoreState) => commonSelectors.selectAllVersionTagsInOrder(state.image),
    selectAllTagsInOrder: (state: StoreState) => commonSelectors.selectAllTagsInOrder(state.image),
    selectInputModalities: (state: StoreState) => commonSelectors.selectInputModalities(state.image),
    selectOutputModalities: (state: StoreState) => commonSelectors.selectOutputModalities(state.image),
}


/** Generate a tree view hierarchy of models and some of their child objects. This is used by the customization page side navigation.
 * This data isn't ready-to-go for Patternfly's TreeView component but the separate convertTreeViewDataIntoItemHierarchy helper function
 * in customization-helpers.tsx is used to generate the appropriate data from what this function produces.
 */
export const selectImageTreeViewItems = createSelector([
    (state: StoreState) => imageCommonSelectors.selectModelsInAlphabeticalOrder(state),
    (state: StoreState) => imageSelectors.selectCustomizationBasesInAlphabeticalOrder(state),
    (state: StoreState) => imageSelectors.selectOutputEntities(state),
    (state: StoreState) => imageSelectors.selectOutputMetadataEntities(state),
    (state: StoreState) => imageSelectors.selectDicomTags(state),
    (state: StoreState) => imageSelectors.selectCustomizationValidationErrorEntities(state),
    (state: StoreState) => imageSelectors.selectFormValidationErrors(state),
    (state: StoreState) => imageSelectors.selectDicomRuleEntities(state),
],
    (models, customizationBases, outputEntities, metadataEntities, dicomTags, validationEntities, formValidationErrors, dicomRuleEntities) => {

        // create an inline validation function that will be supplied to tree view generator function
        const areOutputChildrenValid = (output: CustomizationOutput, validationEntities: Record<string, BackendValidationErrorViewModel>, _formErrors: FormValidationError[]): ValidationStatus => {
            if (!isImageCustomizationOutput(output)) {
                throw new Error('Invalid output type');
            }

            // currently we're only checking that dicom tags have no errors
            const hasBackendError = !dicomTags.filter(t => t.dicomRestrictionId === output.dicomRestriction)
                .every(t => !validationEntities[t.id] || validationEntities[t.id]!.type !== CustomizationObjectType.DicomTag);
            return hasBackendError ? ValidationStatus.Error : ValidationStatus.Valid;
        }

        const results = generateCustomizationTreeViewItems(models, customizationBases, outputEntities, metadataEntities, validationEntities, formValidationErrors, areOutputChildrenValid, dicomRuleEntities);
        return results;
    });

/**
* Factory function for making a memoized selector that returns true if any metadata for given output id is modified.
*/
export const makeSelectIsAnyMetadataForOutputModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [
        (state: StoreState, customizationOutputId: string) => imageSelectors.selectOutputById(state, customizationOutputId),
        (state: StoreState) => imageSelectors.selectOutputMetadataEntities(state)
    ],
    (customizationOutput, metadataEntities) => customizationOutput?.metadata.some(mId => metadataEntities[mId]?.isModified) || false
);

/**
 * Factory function for making a memoized selector that returns true if any selection rule (trigger) for a customization base id is modified.
 */
export const makeSelectIsAnyTriggerForBaseModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [
        (state: StoreState, customizationBaseId) => imageSelectors.selectCustomizationBaseById(state, customizationBaseId),
        (state: StoreState) => imageSelectors.selectAeTitleRuleEntities(state),
        (state: StoreState) => imageSelectors.selectDicomRuleEntities(state)
    ],
    (customizationBase, aeTitleRuleEntities, dicomRuleEntities) => {
        const isAnyAeTitleRuleModified = customizationBase?.aeTitleRules.some(aId => aeTitleRuleEntities[aId]?.isModified) || false;
        if (isAnyAeTitleRuleModified) { return true; }
        return customizationBase?.dicomRules.some(dId => dicomRuleEntities[dId]?.isModified === true) || false;
    }
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific dicom rule. */
export const makeSelectFormValidationErrorsForDicomRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, dicomRuleId: string) => dicomRuleId, imageSelectors.selectFormValidationErrors],
    (dicomRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === dicomRuleId && e.itemType === CustomizationObjectType.DicomRule)
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific dicom attribute rule. */
export const makeSelectFormValidationErrorsForDicomAttributeRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, dicomAttributeRuleId: string) => dicomAttributeRuleId, imageSelectors.selectFormValidationErrors],
    (dicomAttributeRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === dicomAttributeRuleId && e.itemType === CustomizationObjectType.DicomAttributeRule)
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific AE Title rule. */
export const makeSelectFormValidationErrorsForAeTitleRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, aeTitleRuleId: string) => aeTitleRuleId, imageSelectors.selectFormValidationErrors],
    (aeTitleRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === aeTitleRuleId && e.itemType === CustomizationObjectType.AeTitleRule)
);
